import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MerchCartService } from 'src/app/core/services/merch-cart.service';

@Component({
  selector: 'app-cart-popup',
  templateUrl: './cart-popup.component.html',
  styleUrls: ['./cart-popup.component.scss']
})
export class CartPopupComponent implements OnInit {
  @Input() products: any[] = [];
  @Input() count: number = 0;
  @Input() settingsData: any;
  @Input() userInfo: any;
  @Output() closed = new EventEmitter<void>();
  @Output() checkout = new EventEmitter<void>();
  @Output() removed = new EventEmitter<number[]>();
  @Output() openProduct = new EventEmitter<any>();

  pendingId: number = null;
  confirmAll: boolean = false;
  busy: boolean = false;
  private outsideClickEnabled: boolean = false;

  constructor(private merchCartService: MerchCartService, private toastr: ToastrService, private el: ElementRef) { }

  ngOnInit(): void {
    // The click that opened the popup is still bubbling to document; ignore it.
    setTimeout(() => this.outsideClickEnabled = true);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.busy) {
      this.closed.emit();
    }
  }

  // composedPath is captured at dispatch, so buttons that remove themselves on click still count as inside.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.outsideClickEnabled && !this.busy && !event.composedPath().includes(this.el.nativeElement)) {
      this.closed.emit();
    }
  }

  get subtotal(): { amount: number, currency: string } {
    const currencies = new Set((this.products || []).map(product => this.currencyOf(product)));
    if (!this.products?.length || currencies.size !== 1) {
      return null;
    }
    const amount = this.products.reduce((sum, product) => sum + Number(product.price || 0) * Number(product.quantity || 1), 0);
    return { amount, currency: this.currencyOf(this.products[0]) };
  }

  // Line prices are stored in the cart's currency, which can differ from the product's own.
  currencyOf(product: any): string {
    return product?.currency || product?.product_data?.currency_type;
  }

  askRemove(itemId: number): void {
    this.confirmAll = false;
    this.pendingId = itemId;
  }

  askClearAll(): void {
    this.pendingId = null;
    this.confirmAll = true;
  }

  cancel(): void {
    this.pendingId = null;
    this.confirmAll = false;
  }

  remove(toWishlist: boolean): void {
    const items = this.confirmAll ? this.products : this.products.filter(product => product.id === this.pendingId);
    const request = toWishlist
      ? this.merchCartService.moveToWishlist(items, this.userInfo?.access_token, this.confirmAll)
      : this.merchCartService.removeItems(items, this.confirmAll);
    this.busy = true;
    request.subscribe({
      next: (ids) => {
        this.busy = false;
        this.cancel();
        this.toastr.success(toWishlist ? 'Moved to wishlist' : 'Removed from cart');
        this.removed.emit(ids);
      },
      error: (message) => {
        this.busy = false;
        this.toastr.error(typeof message === 'string' ? message : 'Something went wrong, please try again');
      }
    });
  }
}
